import type { Candle, ChartResponse, Interval } from '@invest-copilot/shared';

/**
 * 把即時價 append/update 到 ChartResponse 最後一根 K 棒
 *
 * 規則：
 * - 找到當下時間對齊的 bucket (interval 邊界)
 * - 若 candles 已有該 bucket → 更新 close=price, high=max(high, price), low=min(low, price)
 * - 若沒有 → append 一根新的 (open=上一根 close 或 price 自己, high=price, low=price, close=price)
 */
export function appendLiveBar(
  chart: ChartResponse,
  livePrice: number | undefined,
  interval: Interval,
): ChartResponse {
  if (livePrice == null || !chart.candles.length) return chart;

  const nowSec = Math.floor(Date.now() / 1000);
  const bucketSec = intervalToSeconds(interval);
  if (!bucketSec) return chart;
  const bucketStart = Math.floor(nowSec / bucketSec) * bucketSec;

  const last = chart.candles[chart.candles.length - 1]!;
  const candles = [...chart.candles];

  if (last.timestamp === bucketStart) {
    // 更新最後一根：close = price; high/low 與 price 取 max/min
    candles[candles.length - 1] = {
      ...last,
      close: livePrice,
      high: Math.max(last.high, livePrice),
      low: Math.min(last.low, livePrice),
    };
  } else if (last.timestamp < bucketStart) {
    // 補一根「進行中」的 K 棒
    candles.push({
      timestamp: bucketStart,
      open: last.close,
      high: Math.max(last.close, livePrice),
      low: Math.min(last.close, livePrice),
      close: livePrice,
      volume: 0, // 進行中 K 棒成交量未知（TWSE quote 給的是累計量，不是 bar 內量）
    });
  }
  // 若 last.timestamp > bucketStart（資料超前）— 不動

  return { ...chart, candles };
}

function intervalToSeconds(interval: Interval): number {
  switch (interval) {
    case '1m': return 60;
    case '5m': return 300;
    case '15m': return 900;
    case '30m': return 1800;
    case '60m': return 3600;
    case '1d': return 86400;
    default: return 0;
  }
}

/**
 * K 線時間軸用「台灣時間」統一呈現（不分台股 / 美股）。
 * 美股 9:30 ET 開盤 → 台北 21:30 / 22:30（依夏令時）
 * 使用者要求一律以台灣時間為主。
 */
export function inferTimezone(_symbol: string): string {
  return 'Asia/Taipei';
}

// 避免 lint 警告 — Candle type 用 export 重用
export type { Candle };
